import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { diffWords } from 'diff';
import { textBlocks } from '../lib/docBlocks';

// Word print-layout view — the in-app approximation of the exported .docx.
//
// Mirrors backend/api/exportApi.js block for block: US-Letter pages with 1"
// margins, Times New Roman 12pt justified body at 1.15 line height, centered
// bold uppercase 14pt title, bold keep-with-next headings, "- item" bullets
// with hanging indents, "**line**" bold sub-headings, pipe tables as bordered
// tables, and redlines as tracked-change runs (insertions underlined /
// deletions struck, both in the editing party's color, attribution on hover).
// Structured docs (design docs / clean view of authored specs) mirror the
// export's Calibri + Word-heading-style look.
//
// Pagination is measured, not guessed: every paragraph-level block renders in
// a hidden measurer at the page's content width, real heights pack greedily
// into pages, and keep-with-next headings never orphan at a page bottom. The
// same font metrics the export uses put breaks within a line or two of where
// Word lands them; a block taller than a full page gets a stretched page
// rather than a mid-paragraph split.

// 96dpi geometry: 8.5×11in page, 1in margins (matches the export's 1440-twip
// margins). twips → px is ÷15.
const PAGE_CONTENT_H = 864; // 9in of usable height

const LEGAL_FONT = "'Times New Roman', 'Liberation Serif', Georgia, serif";
const SPEC_FONT = "Calibri, Carlito, 'Segoe UI', Arial, sans-serif";

const BULLET_RE = /^-\s+(.*)$/;
const BOLDLINE_RE = /^\*\*(.+)\*\*$/;
const wSafe = (s) => String(s).replace(/\uf0b7/g, '\u2022');
const splitParas = (text) => String(text || '').split(/\n{2,}|\n/).map((s) => s.trim()).filter(Boolean);

const plain = (text) => [{ t: text }];
const marked = (text, ins) => [ins ? { t: text, ins: true } : { t: text, del: true }];

// diff parts → lines of fragments, split at newlines (the export's
// lineifyParts): the CURRENT text's paragraph structure survives the markup.
function lineifyParts(parts) {
  const lines = [[]];
  for (const p of parts) {
    const segs = String(p.value).split('\n');
    segs.forEach((seg, i) => {
      if (i > 0) lines.push([]);
      if (seg) lines[lines.length - 1].push({ ...p, value: seg });
    });
  }
  return lines;
}

// One clause → paragraph-level blocks. kind: heading | para | bullet |
// boldline | table. Every block carries its clause for anchors + editing.
function clauseBlocks(clause, base, mode, removed, structured) {
  const out = [];
  const push = (kind, runs, extra) => out.push({ kind, runs, clause, ...extra });
  push('heading', plain(clause.heading || ''), { keepNext: true, level: clause.level || 1 });

  if (removed) {
    push('para', marked(String(clause.text || '').replace(/\n+/g, ' '), false));
    return out;
  }

  const baseText = mode === 'redline' && base ? base.text : null;
  const changed = mode === 'redline' && baseText !== null && baseText !== clause.text;
  const wholeNew = mode === 'redline' && (baseText === null || !String(baseText).trim());

  if (changed && !wholeNew) {
    // Tracked-change stream, line-aware (mirrors redlineParagraphs).
    const parts = diffWords(baseText, clause.text);
    for (const frags of lineifyParts(parts)) {
      if (!frags.length) continue;
      const curText = frags.filter((f) => !f.removed).map((f) => f.value).join('');
      const allText = frags.map((f) => f.value).join('');
      const boldLine = BOLDLINE_RE.test(curText.trim()) || BOLDLINE_RE.test(allText.trim());
      const bullet = !boldLine && /^-\s/.test(curText.trim() || allText.trim());
      const runs = [];
      for (const f of frags) {
        const t = wSafe(boldLine ? f.value.replace(/\*\*/g, '') : f.value);
        if (!t) continue;
        runs.push({ t, ins: !!f.added, del: !!f.removed });
      }
      if (runs.length) push(boldLine ? 'boldline' : bullet ? 'bullet' : 'para', runs);
    }
    return out;
  }

  // Clean structure (also wholly-new redline clauses: same blocks, all-ins).
  const mk = (t) => (wholeNew ? marked(t, true) : plain(t));
  for (const block of textBlocks(clause.text)) {
    if (block.type === 'table') {
      push('table', null, { rows: block.rows, header: block.header, ins: wholeNew });
    } else if (structured) {
      for (const line of splitParas(wSafe(block.text))) push('para', mk(line));
    } else {
      for (const line of splitParas(wSafe(block.text))) {
        const b = line.match(BULLET_RE);
        const h = line.match(BOLDLINE_RE);
        if (b) push('bullet', mk(`\u2022 ${b[1]}`));
        else if (h) push('boldline', mk(h[1]), { keepNext: true });
        else push('para', mk(line));
      }
    }
  }
  return out;
}

function runSpans(runs, color, tip) {
  return runs.map((r, i) => {
    if (r.ins) {
      return (
        <ins
          key={i}
          style={{ color, textDecoration: 'underline', textDecorationColor: color, cursor: tip ? 'help' : undefined }}
          data-tip={tip ? `Inserted · ${tip}` : undefined}
        >
          {r.t}
        </ins>
      );
    }
    if (r.del) {
      return (
        <del
          key={i}
          style={{ color, opacity: 0.72, cursor: tip ? 'help' : undefined }}
          data-tip={tip ? `Deleted · ${tip}` : undefined}
        >
          {r.t}
        </del>
      );
    }
    return <span key={i}>{r.t}</span>;
  });
}

// Word heading-style palette for structured docs (Word's built-in H1–H3:
// colored, REGULAR weight — the export's runs set font Calibri, and the
// built-in heading styles carry no bold). Sizes 16/13/12pt; spacing mirrors
// structuredClause (before 320/220tw, after 120tw).
const SPEC_HEADING = {
  1: { fontSize: 21.3, color: '#2F5496', paddingTop: 21.3, marginBottom: 8 },
  2: { fontSize: 17.3, color: '#2F5496', paddingTop: 14.7, marginBottom: 8 },
  3: { fontSize: 16, color: '#1F3763', paddingTop: 14.7, marginBottom: 8 },
};

function WordBlock({ block, structured, color, tip, targeted, anchorIds, onEdit }) {
  const b = block;
  const editable = onEdit && b.clause;
  const common = {
    className: `cs-wordblock${targeted ? ' is-targeted' : ''}`,
    onDoubleClick: editable ? () => onEdit(b.clause) : undefined,
    title: editable ? 'Double-click to edit this section' : undefined,
  };
  const anchors = (anchorIds || []).map((id) => (
    <span key={id} id={id} style={{ display: 'block', height: 0, scrollMarginTop: 76 }} aria-hidden="true" />
  ));

  if (b.kind === 'title') {
    // rTitle in the export: Times New Roman bold caps 14pt, centered, in
    // BOTH modes — structured docs keep the serif title over a Calibri body.
    return (
      <div {...common} style={{ fontFamily: LEGAL_FONT, textAlign: 'center', fontWeight: 700, fontSize: 18.7, marginBottom: 24, textTransform: 'uppercase' }}>
        {anchors}{b.runs[0].t}
      </div>
    );
  }
  if (b.kind === 'toc') {
    // The structured export embeds a Word TOC field (headings 1–3,
    // hyperlinked). Rendered here as Word displays it after updating the
    // field: entries indented per level, dot leaders, page numbers.
    return (
      <div {...common} style={{ fontFamily: SPEC_FONT, margin: '4px 0 25px' }}>
        {anchors}
        {b.entries.map((e, i) => (
          <div
            key={i}
            role="link"
            tabIndex={0}
            style={{ display: 'flex', alignItems: 'baseline', gap: 6, paddingLeft: (e.level - 1) * 14.7, marginBottom: 2, cursor: 'pointer' }}
            onClick={() => document.getElementById(e.anchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            onKeyDown={(ev) => { if (ev.key === 'Enter') document.getElementById(e.anchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
          >
            <span>{e.text}</span>
            <span style={{ flex: 1, borderBottom: '1px dotted #8a8a8a', transform: 'translateY(-3px)' }} aria-hidden="true" />
            <span>{e.page || ''}</span>
          </div>
        ))}
      </div>
    );
  }
  if (b.kind === 'heading') {
    const spec = structured ? SPEC_HEADING[Math.min(b.level || 1, 3)] : null;
    return (
      <div
        {...common}
        style={spec
          ? { fontFamily: SPEC_FONT, fontWeight: 400, ...spec }
          : { fontWeight: 700, paddingTop: 19, marginBottom: 11 }}
      >
        {anchors}{runSpans(b.runs, color, tip)}
      </div>
    );
  }
  if (b.kind === 'table') {
    const mkCell = (text) => (b.ins ? <ins style={{ color, textDecorationColor: color }}>{text}</ins> : text);
    return (
      // The export follows every table with an empty body paragraph — the
      // larger bottom margin reproduces that blank line + its spacing.
      <div {...common} style={{ margin: structured ? '4px 0 25px' : '4px 0 29px' }}>
        {anchors}
        <table className="cs-wordtable" style={{ fontFamily: structured ? SPEC_FONT : LEGAL_FONT }}>
          {b.header && (
            <thead><tr>{b.rows[0].map((c, j) => <th key={j}>{mkCell(c)}</th>)}</tr></thead>
          )}
          <tbody>
            {(b.header ? b.rows.slice(1) : b.rows).map((r, ri) => (
              <tr key={ri}>{r.map((c, ci) => <td key={ci}>{mkCell(c)}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  const style = b.kind === 'bullet'
    ? { paddingLeft: 32, textIndent: -13.3, marginBottom: 4 }
    : b.kind === 'boldline'
      ? { fontWeight: 700, paddingTop: 13, marginBottom: 7 }
      : structured
        ? { marginBottom: 9.3, lineHeight: 1.1 } // export: after 140tw, line 264
        : { textAlign: 'justify', marginBottom: 11 };
  return (
    <div {...common} style={{ whiteSpace: 'pre-wrap', ...style }}>
      {anchors}{runSpans(b.runs, color, tip)}
    </div>
  );
}

export default function WordPages({
  title, clauses, mode, compareById, removedById, structured, versionKey,
  colorFor, tipFor, totalKnown, targetId, chunkSize, onEditClause,
  anchorPrefix = '', // multi-document surfaces (signing ceremony) namespace their anchors
}) {
  const measureRef = useRef(null);
  const [pages, setPages] = useState(null);
  const [fontTick, setFontTick] = useState(0);

  // Repack once real font metrics arrive (Times New Roman can swap in after
  // first paint on a cold cache).
  useEffect(() => {
    let alive = true;
    if (document.fonts?.ready) document.fonts.ready.then(() => { if (alive) setFontTick((t) => t + 1); });
    return () => { alive = false; };
  }, []);

  // The parent rebuilds its maps/arrays every render, but clause text is
  // immutable per version — version ids + the visible clause-id list fully
  // determine content. Keying on this signature keeps blocks stable across
  // unrelated re-renders (scroll tracking etc.), so we only re-measure and
  // repack when the document actually changes.
  const sig = `${title}|${mode}|${structured ? 1 : 0}|${anchorPrefix}|${versionKey || ''}|${clauses.map((c) => c.id).join(',')}`;
  const blocks = useMemo(() => {
    const out = [{ kind: 'title', runs: plain(String(title || 'Agreement').toUpperCase()), clause: null }];
    // Structured exports embed a Word TOC (headings 1–3) right after the
    // title; entries capture their heading's block index so the visible pass
    // can resolve real page numbers once pagination is known.
    const toc = structured ? { kind: 'toc', clause: null, entries: [] } : null;
    if (toc) out.push(toc);
    clauses.forEach((clause, ci) => {
      const removed = removedById ? removedById.has(clause.id) : false;
      const base = compareById ? compareById.get(clause.id) : null;
      const headingIdx = out.length;
      const cb = clauseBlocks(clause, base, mode, removed, structured);
      // Anchors ride the clause's first block: the clause deep-link target +
      // the chunk anchor the reading-position tracker and jumps navigate by.
      cb[0].anchorIds = [
        `${anchorPrefix}clause-${clause.id}`,
        ...(chunkSize && ci % chunkSize === 0 ? [`doc-chunk-${ci / chunkSize + 1}`] : []),
      ];
      cb.forEach((b) => out.push(b));
      if (toc && (clause.level || 1) <= 3) {
        toc.entries.push({
          text: clause.heading || '',
          level: clause.level || 1,
          anchor: `${anchorPrefix}clause-${clause.id}`,
          blockIndex: headingIdx,
        });
      }
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  // Measure every block at content width, then pack into pages. Blocks are
  // flow-roots (CSS), so offsetHeight includes their internal margins.
  useLayoutEffect(() => {
    const el = measureRef.current;
    if (!el) return;
    const heights = Array.from(el.children).map((c) => c.offsetHeight);
    const packed = [];
    let cur = [];
    let h = 0;
    for (let i = 0; i < blocks.length; i += 1) {
      const hi = heights[i] || 0;
      const withNext = blocks[i].keepNext && i + 1 < blocks.length ? hi + (heights[i + 1] || 0) : hi;
      if (cur.length && h + withNext > PAGE_CONTENT_H) {
        packed.push(cur);
        cur = [];
        h = 0;
      }
      cur.push(i);
      h += hi;
    }
    if (cur.length) packed.push(cur);
    setPages(packed);
  }, [blocks, fontTick]);

  const font = structured ? SPEC_FONT : LEGAL_FONT;
  // Block index → page number, for the structured TOC (known after packing;
  // the measurer renders entries without numbers — same height either way).
  const blockPage = {};
  (pages || []).forEach((idxs, pi) => idxs.forEach((i) => { blockPage[i] = pi + 1; }));
  const renderBlock = (i, inMeasure) => {
    let b = blocks[i];
    if (b.kind === 'toc' && !inMeasure) {
      b = { ...b, entries: b.entries.map((e) => ({ ...e, page: blockPage[e.blockIndex] || '' })) };
    }
    const cid = b.clause?.id;
    return (
      <WordBlock
        key={i}
        block={b}
        structured={structured}
        color={cid && colorFor ? colorFor(cid) : 'var(--info)'}
        tip={!inMeasure && cid && tipFor ? tipFor(cid) : ''}
        targeted={!inMeasure && !!cid && cid === targetId}
        anchorIds={inMeasure ? null : b.anchorIds}
        onEdit={inMeasure ? null : onEditClause}
      />
    );
  };

  return (
    // 12pt legal / 11pt spec body at 96dpi; the hidden measurer inherits the
    // same metrics so measured heights match the visible pages exactly.
    <div className="cs-wordpages" style={{ fontFamily: font, fontSize: structured ? 14.7 : 16, lineHeight: 1.15 }}>
      {/* Hidden measurer — same width, same styles, no anchors/interactivity. */}
      <div className="cs-wordmeasure" ref={measureRef} aria-hidden="true">
        {blocks.map((_, i) => renderBlock(i, true))}
      </div>
      {(pages || []).map((idxs, pi) => (
        <div className="cs-wordpage" key={pi}>
          <div className="cs-wordpage-body">{idxs.map((i) => renderBlock(i, false))}</div>
          <div className="cs-wordpage-no">Page {pi + 1}{totalKnown && pages ? ` of ${pages.length}` : ''}</div>
        </div>
      ))}
    </div>
  );
}
